import { supabase } from '@/lib/supabase';

export type GlobalmaxConnection = {
  id: string;
  org_id: string;
  provider: 'globalmax';
  account_token: string | null;
  caller_id: string | null;
  did_number: string | null;
  provisioning_status: 'pending' | 'active' | 'suspended' | 'failed' | null;
  provisioned_at: string | null;
  is_active: boolean;
  globalmax_customer_id: string | null;
  created_at: string;
};

// 32-char hex token (16 random bytes). Matches Globalmax's account_token format.
function randomToken(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export const telephonyApi = {
  async getGlobalmax(orgId: string): Promise<GlobalmaxConnection | null> {
    const { data, error } = await supabase
      .from('telephony_connections')
      .select('*')
      .eq('org_id', orgId)
      .eq('provider', 'globalmax')
      .maybeSingle();
    if (error && error.code !== 'PGRST116') throw error;
    return (data as GlobalmaxConnection) ?? null;
  },

  async createGlobalmax(
    orgId: string,
    input: { did_number?: string; globalmax_customer_id?: string },
  ): Promise<GlobalmaxConnection> {
    const token = randomToken();
    const { data, error } = await supabase
      .from('telephony_connections')
      .insert({
        org_id: orgId,
        provider: 'globalmax',
        account_token: token,
        caller_id: input.did_number ?? null,
        did_number: input.did_number ?? null,
        globalmax_customer_id: input.globalmax_customer_id ?? null,
        provisioning_status: 'pending',
        provisioned_at: new Date().toISOString(),
        is_active: true,
      })
      .select()
      .single();
    if (error) throw error;
    return data as GlobalmaxConnection;
  },

  async rotateToken(id: string): Promise<GlobalmaxConnection> {
    const token = randomToken();
    const { data, error } = await supabase
      .from('telephony_connections')
      .update({ account_token: token, provisioning_status: 'pending' })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as GlobalmaxConnection;
  },

  async setStatus(id: string, status: 'active' | 'suspended'): Promise<void> {
    const { error } = await supabase
      .from('telephony_connections')
      .update({ provisioning_status: status, is_active: status === 'active' })
      .eq('id', id);
    if (error) throw error;
  },

  async updateMeta(
    id: string,
    patch: Partial<Pick<GlobalmaxConnection, 'did_number' | 'caller_id' | 'globalmax_customer_id'>>,
  ): Promise<void> {
    const { error } = await supabase.from('telephony_connections').update(patch).eq('id', id);
    if (error) throw error;
  },

  async remove(id: string): Promise<void> {
    const { error } = await supabase.from('telephony_connections').delete().eq('id', id);
    if (error) throw error;
  },
};
